import { Button } from "@/components/ui/baseUi/button"
import { DropdownInput } from "@/components/ui/forms/dropdownInput"
import { StandardInput } from "@/components/ui/forms/standardInput"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/baseUi/select"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { BoxSubscriptionDetailsEntity } from "@modules/boxSubscription/entities/boxSubscriptionDetailsEntity"
import {
  DurationType,
  type PauseInputFormik,
  pauseYupValidation,
} from "@modules/boxSubscription/formik/boxSubscriptionFormik"
import { useBoxSubscriptionActions } from "@modules/boxSubscription/hooks/useBoxSubscription"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage } from "@modules/formik/formikUtils"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { HUCardAlert } from "@modules/ui/components/huCardAlert"
import { LoggerType } from "@modules/utils/loggerUtils"
import dayjs from "dayjs"
import { useFormik } from "formik"
import { ArrowLeft, Info } from "lucide-react"
import { useEffect, useMemo, useState } from "react"

type Props = {
  memberId: string
  subscription: BoxSubscriptionDetailsEntity
  setIsOfferSupensionVisible: React.Dispatch<React.SetStateAction<boolean>>
  className?: string
} & React.ComponentProps<"div">

export function MemberOfferPauseForm({
  memberId,
  subscription,
  setIsOfferSupensionVisible,
  className,
  ...props
}: Props) {
  const language = useLanguage()

  const [isDisableMonths, setIsDisableMonths] = useState(false)
  const [isResumeVisible, setIsResumeVisible] = useState(false)
  const { pauseSubscription, resumeSubscription, isPending } = useBoxSubscriptionActions(memberId, subscription.id)

  const suspensionTypesOptions = useMemo(() => {
    return [
      { value: DurationType.MONTHLY, label: word("box.member.table.offer.pause.monthlyInputLabel") },
      { value: DurationType.UNLIMITED, label: word("box.member.table.offer.pause.unlimitedInputLabel") },
    ]
  }, [language])

  const formik = useFormik<PauseInputFormik>({
    initialValues: { durationType: DurationType.MONTHLY, months: 1 },
    validationSchema: pauseYupValidation(),
    onSubmit: async () => {
      try {
        await pauseSubscription(formik.values)
        toast.success(word("box.member.table.offer.pause.toastSuccess"), {
          classNames: successToastClassName(),
        })
        setIsResumeVisible(true)
      } catch (e: any) {
        console.error(LoggerType.Service + "pause subscription", e)
        toast.error(word("box.member.table.offer.pause.toastError"), {
          classNames: errorToastClassName(),
        })
      }
    },
  })

  const handleOnSubmit = async () => {
    await formik.submitForm()
  }

  const handleOnResume = async () => {
    await resumeSubscription()
      .then(() => {
        toast.success(word("box.member.table.offer.pause.toastResumeSuccess"), {
          classNames: successToastClassName(),
        })
        setIsResumeVisible(false)
      })
      .catch((e: any) => {
        console.error(LoggerType.Service + "resume subscription", e)
        toast.error(word("box.member.table.offer.pause.toastResumeError"), {
          classNames: errorToastClassName(),
        })
      })
  }

  useEffect(() => {
    if (formik.values.durationType === DurationType.UNLIMITED) {
      setIsDisableMonths(true)
      formik.setFieldValue("months", null)
    } else {
      formik.setFieldValue("months", 1)
      setIsDisableMonths(false)
    }
  }, [formik.values.durationType])

  useEffect(() => {
    if (subscription) {
      const { durationType, months } = subscription.getPause()
      formik.setFieldValue("durationType", durationType)
      formik.setFieldValue("months", months)

      if (subscription.pause) setIsResumeVisible(true)
    }
  }, [subscription])

  const durationText = useMemo(() => {
    if (subscription.pause?.resumeAt) {
      return {
        title: word("box.member.table.offer.resume.defineDuration", {
          count: dayjs(subscription.pause.resumeAt).add(1, "day").diff(dayjs(subscription.pause.pausedAt), "months"),
        }),
        subtitle: `${dayjs(subscription.pause.pausedAt).format("ll")} ${word("global.periodInterval.to")}${" "}${dayjs(
          subscription.pause.resumeAt,
        ).format("ll")}`,
      }
    } else if (subscription.pause) {
      return {
        title: word("box.member.table.offer.resume.unlimitedDuration"),
        subtitle: `  ${word("box.member.table.offer.resume.unlimitedDurationSince")} ${dayjs(
          subscription.pause.pausedAt,
        ).format("LL")}`,
      }
    }
    return { title: "", subtitle: "" }
  }, [subscription])

  return (
    <div className={cn("flex w-full flex-col px-5 pb-6", className)} {...props}>
      {/* HEADER */}
      <div className="flex flex-col gap-6 px-8 py-6">
        <div className="flex">
          <ArrowLeft
            strokeWidth={2.5}
            size={26}
            onClick={() => setIsOfferSupensionVisible(false)}
            className="cursor-pointer"
          />
          <div className="flex grow flex-col items-center justify-center">
            <Text fontStyle="TS" className="text-textDefault-body">
              {word("box.member.table.offer.pause.title")}
            </Text>

            <Text fontStyle="BM" className="text-textDefault-caption">
              {subscription.offer.name}
            </Text>
          </div>
        </div>
      </div>
      <div className="mt-2 mb-8 flex">
        <HUCardAlert
          title={""}
          description={word("box.member.table.offer.pause.alert")}
          icon={<Info size={90} className="mx-2" />}
          className="flex items-center justify-center py-2"
        />
      </div>
      {isResumeVisible ? (
        // RESUME
        <div className="mt-6 flex flex-col">
          <Text fontStyle="TM" className="text-textDefault-body">
            {durationText.title}
          </Text>
          <Text fontStyle="LM" className="mt-2 text-textDefault-caption">
            {durationText.subtitle}
          </Text>

          <div className="mt-20 flex w-full gap-2">
            <Button variant="primary" onClick={() => handleOnResume()} className="w-full" disabled={isPending}>
              {word("box.member.table.offer.pause.resumeButton")}
            </Button>
          </div>
        </div>
      ) : (
        // FORMRIK
        <div className="flex flex-col gap-4">
          <DropdownInput
            label={word("box.member.table.offer.pause.supensionTypeLabel")}
            options={suspensionTypesOptions}
            isRequiredInput={true}
            error={getFormErrorMessage("durationType", formik)}
          >
            <Select
              defaultValue={formik.values.durationType}
              onValueChange={(value) => {
                formik.setFieldValue("durationType", value)
                if (value === DurationType.UNLIMITED) {
                  setIsDisableMonths(true)
                  formik.setFieldValue("months", null)
                } else {
                  formik.setFieldValue("months", 1)
                  setIsDisableMonths(false)
                }
              }}
              items={suspensionTypesOptions}
            >
              <SelectTrigger id={"durationType"}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {suspensionTypesOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DropdownInput>

          <StandardInput
            getFieldProps={formik.getFieldProps}
            name="months"
            error={getFormErrorMessage("months", formik)}
            setFieldValue={formik.setFieldValue}
            inputProps={{
              type: "number",
              inputMode: "numeric",
              step: 1,
              min: 0,
              disabled: isDisableMonths || isPending,
              onChange: (e) => {
                formik.setFieldValue("months", parseInt(e.target.value))
              },
            }}
          />

          <Button variant="primary" size="lg" onClick={() => handleOnSubmit()} className="w-full" disabled={isPending}>
            {word("global.save")}
          </Button>
        </div>
      )}
    </div>
  )
}
